import { Controller } from "@hotwired/stimulus"

// Full-screen photo viewer over a gallery. Each gallery button carries its
// large image URL; the <dialog> shows one at a time with previous/next
// buttons, thumbnails, arrow keys and swipes. Escape and the native dialog
// handle closing, and focus returns to the photo that opened it.
export default class extends Controller {
  static targets = ["dialog", "image", "counter", "source", "thumb", "stage"]
  static values = { counter: String }

  open({ params: { index } }) {
    this.index = index
    this.show()
    this.dialogTarget.showModal()
    document.documentElement.classList.add("overflow-hidden")
  }

  close() {
    this.dialogTarget.close()
  }

  // "close" event — also fires for Escape.
  closed() {
    document.documentElement.classList.remove("overflow-hidden")
  }

  next() {
    this.go(this.index + 1)
  }

  previous() {
    this.go(this.index - 1)
  }

  jump({ params: { index } }) {
    this.go(index)
  }

  key(event) {
    if (event.key === "ArrowRight") this.next()
    if (event.key === "ArrowLeft") this.previous()
  }

  // Clicking the dark area around the photo closes the viewer.
  backdropClick(event) {
    if (event.target === this.dialogTarget || event.target === this.stageTarget) this.close()
  }

  swipeStart(event) {
    if (event.pointerType === "mouse") return
    this.swipeX = event.clientX
  }

  swipeEnd(event) {
    if (this.swipeX == null) return
    const distance = event.clientX - this.swipeX
    this.swipeX = null
    if (Math.abs(distance) < 50) return
    distance < 0 ? this.next() : this.previous()
  }

  go(index) {
    const count = this.sourceTargets.length
    this.index = (index + count) % count
    this.show()
  }

  show() {
    const source = this.sourceTargets[this.index]
    const count = this.sourceTargets.length
    this.imageTarget.src = source.dataset.src
    this.imageTarget.alt = source.dataset.alt
    this.counterTarget.textContent = this.counterValue
      .replace("%{n}", this.index + 1)
      .replace("%{total}", count)

    this.thumbTargets.forEach((thumb, i) => {
      if (i === this.index) {
        thumb.setAttribute("aria-current", "true")
        thumb.scrollIntoView({ block: "nearest", inline: "nearest" })
      } else {
        thumb.removeAttribute("aria-current")
      }
    })

    // Warm the neighbours so flipping feels instant.
    ;[this.index + 1, this.index - 1].forEach((i) => {
      const neighbour = this.sourceTargets[(i + count) % count]
      if (neighbour) new Image().src = neighbour.dataset.src
    })
  }
}
